import { useEffect, useMemo, useState } from 'react';

export interface UseSplashProps {
  isLoading: boolean;
  hospitalLogoUrl: string | undefined;
  isEnabled?: boolean;
  minDisplayDuration?: number;
}

export interface UseSplashResult {
  showSplash: boolean;
  showSpinner: boolean;
  isLogoVisible: boolean;
  splashLogoUrl: string | undefined;
  handleLogoLoad: () => void;
  handleLogoError: () => void;
}

export const useSplash = ({
  isLoading,
  hospitalLogoUrl,
  isEnabled = true,
  minDisplayDuration = 2000,
}: UseSplashProps): UseSplashResult => {
  const [hasMinElapsed, setHasMinElapsed] = useState(false);
  const [hasLogoError, setHasLogoError] = useState(false);
  const [loadedLogoUrl, setLoadedLogoUrl] = useState<string | undefined>();

  // The splash treats a failed logo the same as having no logo at all: keep the spinner visible
  // while upstream loading is active, but stop waiting on the image once loading completes.
  // `effectiveHospitalLogoUrl` is the sanitized input for the splash. The remaining flags derive
  // from that value so the component can move cleanly between these states:
  // spinner only -> spinner + hidden image -> visible image -> exit splash.
  const effectiveHospitalLogoUrl = hasLogoError ? undefined : hospitalLogoUrl;
  const isImageLoaded =
    Boolean(effectiveHospitalLogoUrl) && loadedLogoUrl === effectiveHospitalLogoUrl;
  const isContentReady = !effectiveHospitalLogoUrl || isImageLoaded;
  const showSpinner = !effectiveHospitalLogoUrl || !isImageLoaded;
  const isLogoVisible = isImageLoaded;

  const showSplash = useMemo(() => {
    if (!isEnabled) return false;
    if (isLoading) return true;
    if (!effectiveHospitalLogoUrl) return false;
    if (!isContentReady) return true;
    return !hasMinElapsed;
  }, [isEnabled, isLoading, effectiveHospitalLogoUrl, isContentReady, hasMinElapsed]);

  useEffect(() => {
    setHasLogoError(false);
    setLoadedLogoUrl(undefined);
  }, [hospitalLogoUrl]);

  // Reset min-duration flag at the start of every new loading cycle
  useEffect(() => {
    if (isLoading) {
      setHasMinElapsed(false);
    }
  }, [isLoading]);

  // Start the timer only once loading is done and the logo is actually visible
  useEffect(() => {
    if (!showSplash || isLoading || !effectiveHospitalLogoUrl || !isContentReady) return undefined;

    const timer = setTimeout(() => {
      setHasMinElapsed(true);
    }, minDisplayDuration);

    return () => clearTimeout(timer);
  }, [showSplash, isLoading, effectiveHospitalLogoUrl, isContentReady, minDisplayDuration]);

  return {
    showSplash,
    showSpinner,
    isLogoVisible,
    splashLogoUrl: effectiveHospitalLogoUrl,
    handleLogoLoad: () => setLoadedLogoUrl(effectiveHospitalLogoUrl),
    handleLogoError: () => setHasLogoError(true),
  };
};
